/**
 * Security Settings Tab — IP Access Restriction (CR-5904)
 *
 * Lets client admins manage a per-company IP allowlist:
 *  - Master toggle (with lockout-safety check on enable)
 *  - List, add, edit, activate/deactivate, delete IP / CIDR rules
 *  - Display caller's current request IP so they can self-include before enabling
 */
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  AlertTriangle, Globe, Loader2, Pencil, Plus, ShieldCheck, Trash2,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';
import { Badge } from '../../ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../ui/dialog';
import { Alert, AlertDescription } from '../../ui/alert';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../ui/table';
import {
  securityAPI,
  type IPAccessPolicy,
  type IPAccessRule,
} from '../../../lib/api';
import { MfaSettingsCard } from './MfaSettingsCard';
import { AllowedEmailDomainsCard } from './AllowedEmailDomainsCard';

const validateIpOrCidr = (value: string): string | null => {
  const trimmed = value.trim();
  if (!trimmed) return 'Enter an IP address or CIDR range.';

  const [addr, prefixStr, ...rest] = trimmed.split('/');
  if (rest.length > 0) return 'Invalid format — too many "/".';

  const isIPv4 = (s: string): boolean => {
    const parts = s.split('.');
    if (parts.length !== 4) return false;
    return parts.every(p => /^\d+$/.test(p) && Number(p) >= 0 && Number(p) <= 255);
  };
  const isIPv6 = (s: string): boolean => /^[0-9a-fA-F:]+$/.test(s) && s.includes(':');

  if (!isIPv4(addr) && !isIPv6(addr)) return 'Invalid IP address.';

  if (prefixStr !== undefined) {
    const prefix = Number(prefixStr);
    const max = isIPv4(addr) ? 32 : 128;
    if (!Number.isInteger(prefix) || prefix < 0 || prefix > max) {
      return `CIDR prefix must be between 0 and ${max}.`;
    }
  }
  return null;
};

interface RuleDialogState {
  open: boolean;
  editing: IPAccessRule | null;
  label: string;
  ipValue: string;
  isActive: boolean;
}

const EMPTY_DIALOG: RuleDialogState = {
  open: false,
  editing: null,
  label: '',
  ipValue: '',
  isActive: true,
};

export function SecurityTab() {
  const [loading, setLoading] = useState(true);
  const [savingPolicy, setSavingPolicy] = useState(false);
  const [savingRule, setSavingRule] = useState(false);
  const [policy, setPolicy] = useState<IPAccessPolicy | null>(null);
  const [rules, setRules] = useState<IPAccessRule[]>([]);
  const [myIp, setMyIp] = useState<string>('');
  const [dialog, setDialog] = useState<RuleDialogState>(EMPTY_DIALOG);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [policyRes, rulesRes, ipRes] = await Promise.all([
        securityAPI.getPolicy(),
        securityAPI.listRules(),
        securityAPI.getMyIP(),
      ]);
      setPolicy(policyRes.data);
      const rulesData = rulesRes.data;
      const list = Array.isArray(rulesData) ? rulesData : (rulesData.results ?? []);
      setRules(list);
      setMyIp(ipRes.data.ip || '');
    } catch (err) {
      console.error('Failed to load security settings:', err);
      toast.error('Failed to load security settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleTogglePolicy = async (next: boolean) => {
    if (!policy) return;
    setSavingPolicy(true);
    try {
      const res = await securityAPI.updatePolicy(next);
      setPolicy(res.data);
      toast.success(next ? 'IP access restriction enabled' : 'IP access restriction disabled');
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string; current_ip?: string } } })
        .response?.data?.detail;
      toast.error(detail || 'Failed to update IP access policy', {
        duration: 6000,
      });
    } finally {
      setSavingPolicy(false);
    }
  };

  const openAddDialog = () => setDialog({ ...EMPTY_DIALOG, open: true });

  const openEditDialog = (rule: IPAccessRule) => setDialog({
    open: true,
    editing: rule,
    label: rule.label,
    ipValue: rule.ip_value,
    isActive: rule.is_active,
  });

  const closeDialog = () => setDialog(EMPTY_DIALOG);

  const handleSaveRule = async () => {
    const ipError = validateIpOrCidr(dialog.ipValue);
    if (ipError) {
      toast.error(ipError);
      return;
    }
    if (!dialog.label.trim()) {
      toast.error('A label is required.');
      return;
    }
    setSavingRule(true);
    try {
      if (dialog.editing) {
        const res = await securityAPI.updateRule(dialog.editing.id, {
          label: dialog.label.trim(),
          ip_value: dialog.ipValue.trim(),
          is_active: dialog.isActive,
        });
        setRules(prev => prev.map(r => (r.id === res.data.id ? res.data : r)));
        toast.success('Rule updated');
      } else {
        const res = await securityAPI.createRule({
          label: dialog.label.trim(),
          ip_value: dialog.ipValue.trim(),
          is_active: dialog.isActive,
        });
        setRules(prev => [res.data, ...prev]);
        toast.success('Rule added');
      }
      closeDialog();
      // Refresh policy counts.
      const polRes = await securityAPI.getPolicy();
      setPolicy(polRes.data);
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string; ip_value?: string[] } } })
        .response?.data;
      const message = detail?.detail || detail?.ip_value?.[0] || 'Failed to save rule';
      toast.error(message);
    } finally {
      setSavingRule(false);
    }
  };

  const handleToggleRuleActive = async (rule: IPAccessRule) => {
    try {
      const res = await securityAPI.updateRule(rule.id, { is_active: !rule.is_active });
      setRules(prev => prev.map(r => (r.id === res.data.id ? res.data : r)));
    } catch {
      toast.error('Failed to update rule');
    }
  };

  const handleDeleteRule = async (rule: IPAccessRule) => {
    if (!window.confirm(`Delete rule "${rule.label}"?`)) return;
    setDeletingId(rule.id);
    try {
      await securityAPI.deleteRule(rule.id);
      setRules(prev => prev.filter(r => r.id !== rule.id));
      toast.success('Rule deleted');
      const polRes = await securityAPI.getPolicy();
      setPolicy(polRes.data);
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })
        .response?.data?.detail;
      toast.error(detail || 'Failed to delete rule');
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const enabled = !!policy?.enabled;
  const activeCount = rules.filter(r => r.is_active).length;
  const myIpCovered = rules.some(r => r.is_active && ruleMatches(r, myIp));

  return (
    <div className="space-y-6">
      <AllowedEmailDomainsCard />
      {/* Master toggle */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <ShieldCheck className="h-5 w-5" />
                IP Access Restriction
              </CardTitle>
              <CardDescription className="mt-1">
                When enabled, only requests from the IP addresses and CIDR ranges
                listed below can sign in or use the platform. Superusers and the
                login page itself are always reachable.
              </CardDescription>
            </div>
            <div className="flex items-center gap-3">
              <Badge variant={enabled ? 'default' : 'outline'} className={enabled ? 'bg-emerald-600' : ''}>
                {enabled ? 'Enforced' : 'Disabled'}
              </Badge>
              <Switch
                checked={enabled}
                disabled={savingPolicy}
                onCheckedChange={handleTogglePolicy}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Globe className="h-4 w-4" />
            <span>Your current IP:&nbsp;</span>
            <span className="font-mono font-semibold text-foreground">{myIp || 'unknown'}</span>
            {enabled && myIp && (
              myIpCovered
                ? <Badge variant="outline" className="border-emerald-300 text-emerald-700">In allowlist</Badge>
                : <Badge variant="outline" className="border-amber-400 text-amber-700">Not in allowlist</Badge>
            )}
          </div>
          {!enabled && rules.length === 0 && (
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                Add at least one IP rule that covers your current IP before enabling
                restriction, or you will be locked out and need a superuser to recover.
              </AlertDescription>
            </Alert>
          )}
          {enabled && activeCount === 0 && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                Restriction is enabled but no active rules exist — the policy is
                effectively bypassed. Add rules below.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Rule list */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle>Allowed IP Addresses & Ranges</CardTitle>
            <CardDescription>
              {rules.length === 0 ? 'No rules configured.' :
                `${rules.length} rule${rules.length === 1 ? '' : 's'} (${activeCount} active)`}
            </CardDescription>
          </div>
          <Button onClick={openAddDialog} size="sm">
            <Plus className="h-4 w-4 mr-1.5" />
            Add IP / Range
          </Button>
        </CardHeader>
        <CardContent>
          {rules.length === 0 ? (
            <div className="text-center py-10 text-sm text-muted-foreground border-2 border-dashed rounded-md">
              No IP rules yet. Click <span className="font-medium">Add IP / Range</span> to create one.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Label</TableHead>
                  <TableHead>IP / CIDR</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Active</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rules.map(rule => (
                  <TableRow key={rule.id}>
                    <TableCell className="font-medium">{rule.label}</TableCell>
                    <TableCell className="font-mono text-sm">{rule.ip_value}</TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {rule.rule_type === 'cidr' ? 'CIDR Range' : 'Single IP'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={rule.is_active}
                        onCheckedChange={() => handleToggleRuleActive(rule)}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost" size="icon" className="h-8 w-8"
                        onClick={() => openEditDialog(rule)}
                        title="Edit"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive"
                        disabled={deletingId === rule.id}
                        onClick={() => handleDeleteRule(rule)}
                        title="Delete"
                      >
                        {deletingId === rule.id
                          ? <Loader2 className="h-4 w-4 animate-spin" />
                          : <Trash2 className="h-4 w-4" />}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* CR-5275: per-role MFA toggle. Lives in the Security tab alongside
          IP allowlisting since it's the same admin audience. */}
      <MfaSettingsCard />

      {/* Add/Edit dialog */}
      <Dialog open={dialog.open} onOpenChange={(open) => !open && closeDialog()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{dialog.editing ? 'Edit IP Rule' : 'Add IP Rule'}</DialogTitle>
            <DialogDescription>
              Enter a single IP address (e.g. <code>203.0.113.10</code>) or a CIDR
              range (e.g. <code>203.0.113.0/24</code>). IPv6 is supported.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="rule-label">Label</Label>
              <Input
                id="rule-label"
                placeholder="e.g. London Office VPN"
                value={dialog.label}
                onChange={e => setDialog(d => ({ ...d, label: e.target.value }))}
                maxLength={120}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="rule-ip">IP Address or CIDR</Label>
              <Input
                id="rule-ip"
                placeholder="203.0.113.10  or  203.0.113.0/24"
                value={dialog.ipValue}
                onChange={e => setDialog(d => ({ ...d, ipValue: e.target.value }))}
                className="font-mono"
              />
              {!dialog.editing && myIp && (
                <button
                  type="button"
                  className="text-xs text-primary hover:underline"
                  onClick={() => setDialog(d => ({ ...d, ipValue: myIp, label: d.label || 'My current IP' }))}
                >
                  Use my current IP ({myIp})
                </button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="rule-active"
                checked={dialog.isActive}
                onCheckedChange={(checked) => setDialog(d => ({ ...d, isActive: checked }))}
              />
              <Label htmlFor="rule-active" className="cursor-pointer">Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog} disabled={savingRule}>Cancel</Button>
            <Button onClick={handleSaveRule} disabled={savingRule}>
              {savingRule && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {dialog.editing ? 'Save changes' : 'Add rule'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ruleMatches(rule: IPAccessRule, ip: string): boolean {
  if (!ip || !rule.is_active) return false;
  const value = rule.ip_value;
  if (!value.includes('/')) return value === ip;
  // Lightweight CIDR membership for IPv4 only — UI hint, not enforcement.
  const [network, prefixStr] = value.split('/');
  const prefix = Number(prefixStr);
  const ipNum = ipv4ToNumber(ip);
  const netNum = ipv4ToNumber(network);
  if (ipNum === null || netNum === null) return false;
  if (prefix === 0) return true;
  const mask = prefix === 32 ? 0xffffffff : ((-1 << (32 - prefix)) >>> 0);
  return (ipNum & mask) === (netNum & mask);
}

function ipv4ToNumber(ip: string): number | null {
  const parts = ip.split('.');
  if (parts.length !== 4) return null;
  let result = 0;
  for (const p of parts) {
    if (!/^\d+$/.test(p)) return null;
    const n = Number(p);
    if (n < 0 || n > 255) return null;
    result = (result << 8) | n;
  }
  return result >>> 0;
}
